import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import _ from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  createPermissionGroup,
  getPermissionsList,
  updatePermissionGroup,
} from '../../api/ClientManagementService';
import {
  BUDGET_ESTIMATED,
  EVIDENCE_SOURCE_MANAGEMENT,
  EVIDENCE_UPLOAD_PERMISSION_TYPE,
  MAX_CHAR,
  MAX_CHAR_LIMIT,
  PERMISSION_GROUP_CATEGORY,
  PERMISSION_TYPE,
} from '../../constants/constants';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {
  getPermissionGroups,
  setPermissionGroup,
  addNewPermissionGroup,
  getAllPermissionGroups,
} from '../../redux/slices/permissionGroupSlice';
import {getPermission, setPermission} from '../../redux/slices/permissionSlice';
import {
  getPermissions,
  permissionState,
  setPermissions,
} from '../../redux/slices/permissionsSlice';
import {Permission} from '../../types/permission-group/permission';
import {isEmpty, isEmptyInstance} from '../../utils/utils';
import AurAskLioDrawerHeaderButton from '../aur-ask-lio/AurAskLioDrawerHeaderButton';
import AurButton from '../common/aur-button/AurButton';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import {AurSwitch} from '../common/aur-switch/AurSwitch';
import {AurTextField} from '../common/aur-text-field/AurTextField';
import EstimatedBudgetView, {
  EstimatedBudgetValue,
} from './components/EstimatedBudgetView';
import EvidenceSourceManageView, {
  EvidenceSourceManageValue,
} from './components/EvidenceSourceManageView';
import {
  FeatureSettingEnum,
  isFeatureSettingEnabled,
} from '../../redux/slices/featureSettingsSlice';
import {RootState} from '../../redux/store';
import {PermissionGroup} from '../../types/permission-group/permissionGroup';
import PermissionCategoryView from './components/PermissionCategoryView';
import {
  excludedPermissionCodes,
  usePermissionGroupConfig,
  PermissionType,
} from '../../utils/permissionListUtils';
import {ReactComponent as InfoIcon} from '../../assests/svg/info_icon_outlined_blue.svg';
import AurTooltip from '../common/aur-tooltip/AurTooltip';
import {QSI_BTN_IDS} from '../../constants/qualtricsConstants';
import {setPermissionGroupDrawerDntSaveDialogConfirmBtnId} from '../../redux/slices/qualtricsSlice';

interface AddEditPermissionGroupProps {
  operationCode: OperationCode;
  category: PERMISSION_GROUP_CATEGORY;
  permissionGroupId?: string;
  onClose: () => void;
  onCancel: () => void;
  onAddSuccess: (savedPermissionGroup?: PermissionGroup) => void;
  clientId: string;
  budgetConsumptionEnabled: boolean;
}

export type GroupPermValueItem = Record<
  string,
  {
    permissionId: string;
    permissionCode: string;
    checked: boolean;
  }
>;

const getSavedPermissionGroupFromResponse = (response: {
  data?: {data?: PermissionGroup & {value?: PermissionGroup}};
}): PermissionGroup | undefined => {
  const dto = response?.data?.data?.value ?? response?.data?.data;
  if (!dto?.permissionGroupId) {
    return undefined;
  }

  return {
    permissionGroupId: dto.permissionGroupId,
    name: dto.name,
    description: dto.description ?? '',
    clientId: dto.clientId,
    externalClientId: dto.externalClientId ?? '',
    category: dto.category ?? '',
    permissions: dto.permissions ?? [],
    users: dto.users ?? [],
  };
};

export const enum OperationCode {
  add = 'add',
  edit = 'edit',
}

interface SwitchLabelProps {
  label: string;
  state: boolean;
  footerNotes?: string;
  infoText?: string;
  onChange: () => void;
  permCategory: PERMISSION_GROUP_CATEGORY;
  disableToolTip?: boolean;
}

type FixedHeaderPanelContainerProps = {
  panelHeader?: React.ReactNode;
  contentHeader?: React.ReactNode;
  contentBody?: React.ReactNode;
};

export const DEFAULT_ECI_ENABLED_PERMISSION_CODES = [
  'ECI_SEARCHES',
  'ECI_SAVED_SEARCHES',
  'ECI_DOCUMENT_PREVIEW',
  'ECI_DOCUMENT_TAGGING',
];

export const SwitchWithLabel: React.FC<SwitchLabelProps> = ({
  label,
  state,
  footerNotes,
  infoText,
  onChange,
  permCategory,
  disableToolTip = false,
}) => (
  <div
    className={`switch-row ${
      permCategory === PERMISSION_GROUP_CATEGORY.CLIENT && 'client-switch-row'
    }`}
  >
    <div className="switch-row-content">
      <AurSwitch
        checked={state}
        onChange={onChange}
        ariaLabel={`${t('aria.toggle')} ${label}`}
      />
      <Box className="switch-text-info-icon">
        <Box>
          <Typography className="typo-text subhead4_size row-text">
            {label}
          </Typography>
          {isEmptyInstance(footerNotes) ? null : (
            <Typography className="typo-text body4_size row-text switch-sub-text">
              {footerNotes}
            </Typography>
          )}
        </Box>
        {infoText && (
          <AurTooltip
            title={infoText}
            aria-label={infoText}
            tabIndex={0}
            disableTouchListener={disableToolTip}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -7],
                    },
                  },
                ],
              },
            }}
            trigger="click"
          >
            <InfoIcon className="permission-info-icon" />
          </AurTooltip>
        )}
      </Box>
    </div>
  </div>
);

const FixedHeaderPanelContainer = ({
  panelHeader,
  contentHeader,
  contentBody,
}: FixedHeaderPanelContainerProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | string>('');

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight ?? 0;
    const availableHeight = window.innerHeight - headerHeight;
    setContentHeight(availableHeight);
  }, []);
  return (
    <Box
      className="add-edit-scrollbar"
      role="main"
      aria-label={t('aria.permissionGroupMgmt')}
    >
      <Box
        ref={headerRef}
        className="panel-header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          flexShrink: 0,
          backgroundColor: 'inherit',
        }}
      >
        {panelHeader}
      </Box>

      <Box
        sx={{
          height: contentHeight,
        }}
      >
        <Box className="content-header">{contentHeader || null}</Box>
        {contentBody || null}
      </Box>
    </Box>
  );
};

const AddEditPermissionGroup: React.FC<AddEditPermissionGroupProps> = ({
  operationCode,
  permissionGroupId,
  category,
  onClose,
  onAddSuccess,
  clientId,
  budgetConsumptionEnabled,
  onCancel,
}) => {
  const [groupName, setGroupName] = useState<string>('');
  const [groupNameError, setGroupNameError] = useState<string | null>(null);
  const [description, setDescription] = useState<string>('');
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [operationCodeState, setOperationCodeState] =
    useState<OperationCode>(operationCode);
  const [orgGroupPermValueItems, seOrgGroupPermValueItems] = useState<
    Record<PermissionType, GroupPermValueItem>
  >({});

  const [, setOrgPermCategoryEnabledObj] = useState<
    Record<PermissionType, boolean>
  >({});

  const [groupPermValueItems, setGroupPermValueItems] = useState<
    Record<PermissionType, GroupPermValueItem>
  >({});

  const [permCategoryEnabledObj, setPermCategoryEnabledObj] = useState<
    Record<PermissionType, boolean>
  >({});

  const [budgetPermissionKeyArray, setBudgetPermissionKeyArray] = useState<
    string[]
  >([]);

  const [
    evidenceUploadPermissionKeyArray,
    setEvidenceUploadPermissionKeyArray,
  ] = useState<string[]>([]);

  const [permCategory, setPermCategory] =
    useState<PERMISSION_GROUP_CATEGORY>(category);

  const {showToast} = useToast();
  const dispatch = useDispatch();

  const permissionsFromDB = useSelector(getPermissions);
  const permissionGroups = useSelector(getPermissionGroups);
  const allPermissionGroups = useSelector(getAllPermissionGroups);

  const {
    description: originalDescription, //Renaming properties to avoid naming conflict
    groupName: originalGroupName,
    budgetPermissionKeys: originalBudgetPermissionKeys,
    evidenceUploadPermissionKeys: originalEvidenceUploadPermissionKeys,
  } = useSelector(getPermission);

  const isEvidenceSourceUploadEnabled = useSelector((state: RootState) =>
    isFeatureSettingEnabled(state, FeatureSettingEnum.EvidenceSourceUpload)
  );

  const isPowerBIReportsEnabled = useSelector((state: RootState) =>
    isFeatureSettingEnabled(state, FeatureSettingEnum.PowerBIReportsEnabled)
  );

  const {permissionMasterConfig, permissionList} = usePermissionGroupConfig();

  const isProjectPerm = useMemo(
    () => permCategory === PERMISSION_GROUP_CATEGORY.PROJECT,
    [permCategory]
  );

  const isClientPerm = useMemo(
    () => permCategory === PERMISSION_GROUP_CATEGORY.CLIENT,
    [permCategory]
  );

  const existingGroupNames = useMemo(
    () => (allPermissionGroups ?? []).map((group) => group.name.toLowerCase()),
    [allPermissionGroups]
  );

  const permissionsMap = useMemo(
    () =>
      new Map(
        permissionsFromDB?.map((permission) => [
          permission.permissionCode,
          permission,
        ])
      ),
    [permissionsFromDB]
  );

  const updatedPermissionList = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(permissionList).map(([type, permissionItems]) => [
          type,
          permissionItems
            ?.filter((item) => permissionsMap.has(item.permissionCode))
            ?.map((item) => ({
              ...permissionsMap.get(item.permissionCode),
              ...item,
            })),
        ])
      ),
    [permissionList, permissionsMap]
  );

  const updatePermCategoryEnabledObj = useCallback(
    (permissions: Permission[]) => {
      setPermCategoryEnabledObj((_) => {
        const updatedPermCategoryEnabledObj: Record<PermissionType, boolean> =
          {};
        const uniqueTypes = [...new Set(permissions.map((item) => item.type))];
        (permissionMasterConfig?.[permCategory]?.perm_categories ?? []).forEach(
          (item: PermissionType) => {
            const permCatConfig =
              permissionMasterConfig?.[permCategory]?.perm_cat_config?.[item];
            updatedPermCategoryEnabledObj[item] =
              (permCatConfig?.isOverriden ?? false)
                ? (uniqueTypes.includes(item) ??
                  permCatConfig?.defaultIsOverridenChecked ??
                  false)
                : true;
          }
        );
        return {
          ...updatedPermCategoryEnabledObj,
        };
      });
    },
    [permissionMasterConfig, updatedPermissionList, permCategory]
  );

  useEffect(() => {
    if (operationCodeState === OperationCode.add) {
      updatePermCategoryEnabledObj([]);
    }
  }, [operationCodeState, permCategory]);

  useEffect(() => {
    setOrgPermCategoryEnabledObj((_) => {
      const updatedPermCategoryEnabledObj: Record<PermissionType, boolean> = {};
      (permissionMasterConfig?.[permCategory]?.perm_categories ?? []).forEach(
        (item: PermissionType) => {
          const permCatConfig =
            permissionMasterConfig?.[permCategory]?.perm_cat_config?.[item];
          updatedPermCategoryEnabledObj[item] =
            (permCatConfig?.isOverriden ?? false)
              ? isEmptyInstance(orgGroupPermValueItems?.[item] ?? {})
              : true;
        }
      );
      return {
        ...updatedPermCategoryEnabledObj,
      };
    });
  }, [orgGroupPermValueItems, permCategory, permissionMasterConfig]);

  const evidenceUploadPermissions = useMemo(
    () =>
      (permissionsFromDB ?? []).filter((permission: Permission) =>
        [PERMISSION_TYPE.EVIDENCE].includes(permission?.type)
      ),
    [permissionsFromDB]
  );

  const budgetPermissions = useMemo(
    () =>
      (permissionsFromDB ?? []).filter((permission: Permission) =>
        [PERMISSION_TYPE.BUDGET].includes(permission?.type)
      ),
    [permissionsFromDB]
  );

  const budgetPermissionIdObj: Record<string, string> = useMemo(
    () =>
      Object.assign(
        {},
        ...Object.values(BUDGET_ESTIMATED).map((item: string) => {
          const permissionObj = budgetPermissions.find(
            (permission: Permission) => permission?.permissionCode === item
          );
          return {
            [item]: permissionObj?.permissionId ?? '',
          };
        })
      ),
    [budgetPermissions]
  );

  const evidenceUploadPermissionIdObj: Record<string, string> = useMemo(
    () =>
      Object.assign(
        {},
        ...Object.values(EVIDENCE_SOURCE_MANAGEMENT).map((item: string) => {
          const permissionObj = evidenceUploadPermissions.find(
            (permission: Permission) => permission?.permissionCode === item
          );
          return {
            [item]: permissionObj?.permissionId ?? '',
          };
        })
      ),
    [evidenceUploadPermissions]
  );

  const fetchPermissions = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await getPermissionsList();
      const filteredPermissions = result?.data?.data?.value?.permissions.filter(
        (perm: Permission) => {
          return !excludedPermissionCodes
            .map((item) => item.toString())
            .includes(perm.permissionCode);
        }
      );

      const filteredPerms: permissionState = {
        ...result?.data?.data,
        value: {
          permissions: filteredPermissions,
        },
      };

      dispatch(setPermissions(filteredPerms));
    } catch (e) {
      console.log('Error', e);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const getSelectedPermissionObjects = useCallback(
    (selectedIds: string[]): Permission[] => {
      const allPermissions = [...(permissionsFromDB ?? [])];

      return allPermissions.filter((permission) =>
        selectedIds.includes(permission?.permissionId)
      );
    },
    [permissionsFromDB]
  );

  const savePermissionGroup = useCallback(async () => {
    setIsSaving(true);

    const selectedPermissions: string[] = [];

    for (const [key, value] of Object.entries(permCategoryEnabledObj)) {
      if (value) {
        selectedPermissions.push(
          ...Object.values(groupPermValueItems?.[key])
            .filter((item) => item.checked)
            .map((item) => item.permissionId)
        );
      }
    }

    const selectedBudgetPermissionIdArray: string[] = budgetPermissionKeyArray
      .map((item: string) => budgetPermissionIdObj?.[item] ?? '')
      .filter((item) => !isEmpty(item));

    selectedPermissions.push(...selectedBudgetPermissionIdArray);

    const selectedEvidenceUploadPermissionIdArray: string[] =
      evidenceUploadPermissionKeyArray
        .map((item: string) => evidenceUploadPermissionIdObj?.[item] ?? '')
        .filter((item) => !isEmpty(item));

    selectedPermissions.push(...selectedEvidenceUploadPermissionIdArray);

    const combinedPermissions: string[] = [...selectedPermissions];

    const finalPermissions: string[] = Array.from(new Set(combinedPermissions));

    const data = {
      id: permissionGroupId,
      name: groupName,
      description,
      clientId,
      category: permCategory,
      permissions: finalPermissions,
    };

    try {
      if (operationCodeState === OperationCode.add) {
        const response = await createPermissionGroup(clientId, data);

        const savedPermissionGroup = getSavedPermissionGroupFromResponse(
          response
        ) ?? {
          permissionGroupId: '',
          name: groupName,
          description,
          clientId,
          externalClientId: '',
          permissions: getSelectedPermissionObjects(finalPermissions),
          users: [],
          category: permCategory,
        };

        dispatch(addNewPermissionGroup(savedPermissionGroup));

        showToast(t('toast.permissionGroupAdded'), 'success');

        onAddSuccess(savedPermissionGroup);
        return;
      } else {
        const response = await updatePermissionGroup(
          clientId,
          permissionGroupId ?? '',
          data
        );
        const savedPermissionGroup = getSavedPermissionGroupFromResponse(
          response
        ) ?? {
          permissionGroupId: permissionGroupId ?? '',
          name: groupName,
          description: description,
          clientId: clientId,
          externalClientId: '',
          permissions: getSelectedPermissionObjects(finalPermissions),
          users: [],
          category: permCategory,
        };

        dispatch(
          setPermissionGroup({
            permissionGroupId: savedPermissionGroup.permissionGroupId,
            name: savedPermissionGroup.name,
            description: savedPermissionGroup.description,
            permissions: savedPermissionGroup.permissions,
          })
        );

        showToast(t('toast.permissionGroupUpdated'), 'success');
        onAddSuccess(savedPermissionGroup);
        return;
      }
    } catch (e) {
      console.error('Error saving permission group:', e);
      showToast(t('toast.permissionGroupSaveFailed'), 'error');
    } finally {
      setIsSaving(false);
    }
  }, [
    budgetPermissionIdObj,
    budgetPermissionKeyArray,
    clientId,
    description,
    dispatch,
    evidenceUploadPermissionIdObj,
    evidenceUploadPermissionKeyArray,
    getSelectedPermissionObjects,
    groupName,
    groupPermValueItems,
    onAddSuccess,
    operationCodeState,
    permCategory,
    permCategoryEnabledObj,
    permissionGroupId,
    showToast,
  ]);

  const validateSpecialCharacters = useCallback((value: string) => {
    const regex = /^[a-zA-Z0-9_-]+(?: [a-zA-Z0-9_-]+)*$/;
    return regex.test(value);
  }, []);

  const handlePermissionGroupNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const trimmedValue = value.trim().toLowerCase();
      if (!validateSpecialCharacters(value) || value.length > MAX_CHAR_LIMIT) {
        setGroupNameError(
          t('addNewPermissionGroupPage.permissionGroupNameMsg')
        );
      } else if (
        existingGroupNames.includes(trimmedValue) &&
        trimmedValue !== originalGroupName.trim().toLowerCase()
      ) {
        setGroupNameError(
          t('addNewPermissionGroupPage.permissionGroupDuplicateMsg')
        );
      } else {
        setGroupNameError(null);
      }

      setGroupName(value);
    },
    [existingGroupNames, originalGroupName, validateSpecialCharacters]
  );

  const handlePermissionDescriptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (!value.trim()) {
        setDescriptionError('');
      } else if (value.length > MAX_CHAR) {
        setDescriptionError(
          t('addNewPermissionGroupPage.descriptionValidationMsg')
        );
      } else {
        setDescriptionError(null);
      }

      setDescription(value);
    },
    []
  );

  const buildPermissionsByPermType = useCallback(
    (
      permissionType: PermissionType,
      mode: OperationCode,
      selectedPermissions?: Permission[]
    ) => {
      const selectedCodes =
        selectedPermissions?.map((p) => p.permissionCode) ?? [];

      const permissions = (permissionsFromDB ?? []).filter(
        (p) => p.type === permissionType
      );

      const updatedPermSwitches: GroupPermValueItem = permissions.reduce(
        (acc: GroupPermValueItem, perm: Permission) => {
          acc[perm.permissionId] = {
            permissionId: perm.permissionId,
            permissionCode: perm.permissionCode,
            checked:
              mode === OperationCode.add
                ? false
                : selectedCodes.includes(perm.permissionCode),
          };
          return acc;
        },
        {}
      );

      return updatedPermSwitches;
    },
    [permissionsFromDB]
  );

  const buildAllPermissions = useCallback(
    (mode: OperationCode, selectedPermissions?: Permission[]) => {
      if (permissionMasterConfig) {
        setGroupPermValueItems((prevGroupPermValueItems) => {
          const updatedGroupPermValueItemArray: Record<
            PermissionType,
            GroupPermValueItem
          >[] = (
            permissionMasterConfig?.[permCategory]?.perm_categories ?? []
          ).map((permissionType: PermissionType) => ({
            [permissionType]: {
              ...buildPermissionsByPermType(
                permissionType,
                mode,
                selectedPermissions
              ),
            },
          }));

          const updatedGroupPermValueItems = {
            ...prevGroupPermValueItems,
            ...Object.assign({}, ...updatedGroupPermValueItemArray),
          };

          return updatedGroupPermValueItems;
        });
      }
    },
    [buildPermissionsByPermType, permCategory, permissionMasterConfig]
  );

  const getPermissionGroupById = useCallback(
    (permissionGroupId: string) => {
      try {
        const permissionGroup = allPermissionGroups?.find(
          (x) =>
            x.clientId == clientId && x.permissionGroupId == permissionGroupId
        );

        if (permissionGroup) {
          setGroupName(permissionGroup.name);

          dispatch(
            setPermission({
              property: 'groupName',
              value: permissionGroup.name,
            })
          );
          setDescription(permissionGroup.description);

          dispatch(
            setPermission({
              property: 'description',
              value: permissionGroup.description,
            })
          );

          const initBudgetPermissionKeyArray: string[] = (
            permissionGroup?.permissions ?? []
          )
            .filter(
              (permission: Permission) =>
                permission?.type === PERMISSION_TYPE.BUDGET
            )
            .map((item: Permission) => item?.permissionCode ?? '');

          setBudgetPermissionKeyArray([...initBudgetPermissionKeyArray]);

          dispatch(
            setPermission({
              property: 'budgetPermissionKeys',
              value: [...initBudgetPermissionKeyArray],
            })
          );

          const initEvidenceUploadPermissionKeyArray: string[] = (
            permissionGroup?.permissions ?? []
          )
            .filter(
              (permission: Permission) =>
                permission?.type === EVIDENCE_UPLOAD_PERMISSION_TYPE
            )
            .map((item: Permission) => item?.permissionCode ?? '');

          setEvidenceUploadPermissionKeyArray([
            ...initEvidenceUploadPermissionKeyArray,
          ]);

          dispatch(
            setPermission({
              property: 'evidenceUploadPermissionKeys',
              value: [...initEvidenceUploadPermissionKeyArray],
            })
          );

          setGroupPermValueItems((prevGroupPermValueItems) => {
            const updatedGroupPermValueItemArray: Record<
              PermissionType,
              GroupPermValueItem
            >[] = (
              permissionMasterConfig?.[permCategory]?.perm_categories ?? []
            ).map((permissionType: PermissionType) => ({
              [permissionType]: {
                ...buildPermissionsByPermType(
                  permissionType,
                  OperationCode.edit,
                  permissionGroup?.permissions ?? []
                ),
              },
            }));

            const updatedGroupPermValueItems = {
              ...prevGroupPermValueItems,
              ...Object.assign({}, ...updatedGroupPermValueItemArray),
            };

            return updatedGroupPermValueItems;
          });
        }
        if (!permissionGroup) {
          throw new Error(
            `Permission group with ID ${permissionGroupId} not found.`
          );
        }

        return permissionGroup;
      } catch (error) {
        console.error('Error fetching permission group:', error);
        throw error;
      }
    },
    [
      buildPermissionsByPermType,
      clientId,
      dispatch,
      permCategory,
      permissionGroups,
      permissionMasterConfig,
    ]
  );

  useEffect(() => {
    fetchPermissions();
  }, []);

  const updateAllPermissions = useCallback(
    (mode: OperationCode, selectedPermissions?: Permission[]) => {
      if (permissionMasterConfig) {
        seOrgGroupPermValueItems((prevGroupPermValueItems) => {
          const updatedGroupPermValueItemArray: Record<
            PermissionType,
            GroupPermValueItem
          >[] = (
            permissionMasterConfig?.[permCategory]?.perm_categories ?? []
          ).map((permissionType: PermissionType) => ({
            [permissionType]: {
              ...buildPermissionsByPermType(
                permissionType,
                mode,
                selectedPermissions
              ),
            },
          }));

          const updatedGroupPermValueItems = {
            ...prevGroupPermValueItems,
            ...Object.assign({}, ...updatedGroupPermValueItemArray),
          };

          return updatedGroupPermValueItems;
        });
      }
    },
    [buildPermissionsByPermType, permCategory, permissionMasterConfig]
  );

  useEffect(() => {
    if (
      operationCodeState === OperationCode.add &&
      !isEmptyInstance(permissionsFromDB)
    ) {
      buildAllPermissions(OperationCode.add);
    }
  }, [operationCodeState, buildAllPermissions, permissionsFromDB]);

  useEffect(() => {
    if (operationCodeState === OperationCode.add) {
      updateAllPermissions(operationCodeState);
    }
  }, [operationCodeState, updateAllPermissions]);

  const getBudgetPermissionKeyArray = useCallback(
    (budgetValue: EstimatedBudgetValue) => {
      const budgetValueMappingObj: Record<keyof EstimatedBudgetValue, string> =
        {
          rightToViewEstimatedBudget:
            BUDGET_ESTIMATED.REPORT_BUDGET_CONSUMPTION,
          showBudgetAlerts: BUDGET_ESTIMATED.REPORT_BUDGET_ALERT,
        };

      const budgetKeyArray: string[] = [];

      for (const key in {...budgetValue}) {
        if ({...budgetValue}?.[key] ?? false) {
          budgetKeyArray.push({...budgetValueMappingObj}?.[key] ?? '');
        }
      }
      return budgetKeyArray.filter((item) => !isEmpty(item));
    },
    []
  );

  const getEvidenceUploadPermissionKeyArray = useCallback(
    (evidenceValue: EvidenceSourceManageValue) => {
      const evidenceValueMappingObj: Record<
        keyof EvidenceSourceManageValue,
        string
      > = {
        manageProjectEvidence: EVIDENCE_SOURCE_MANAGEMENT.STAGING_AREA_ACCESS,
        uploadRights: EVIDENCE_SOURCE_MANAGEMENT.EVIDENCE_SOURCE_UPLOAD_ACCESS,
        initiateProcessingRights:
          EVIDENCE_SOURCE_MANAGEMENT.INITIATE_PROCESSING_ACCESS,
      };

      const evidenceUploadKeyArray: string[] = [];

      for (const key in {...evidenceValue}) {
        if ({...evidenceValue}?.[key] ?? false) {
          evidenceUploadKeyArray.push(
            {...evidenceValueMappingObj}?.[key] ?? ''
          );
        }
      }
      return evidenceUploadKeyArray.filter((item) => !isEmpty(item));
    },
    []
  );

  const onChangeBudgetValue = useCallback(
    (budgetValue: EstimatedBudgetValue) => {
      setBudgetPermissionKeyArray(
        getBudgetPermissionKeyArray({
          ...budgetValue,
          ...(!(budgetValue?.rightToViewEstimatedBudget ?? false)
            ? {showBudgetAlerts: false}
            : {}),
        })
      );
    },
    [getBudgetPermissionKeyArray]
  );

  const onChangeEvidenceSourceManageValue = useCallback(
    (evidenceSourceManageValue: EvidenceSourceManageValue) => {
      setEvidenceUploadPermissionKeyArray(
        getEvidenceUploadPermissionKeyArray({
          ...evidenceSourceManageValue,
        })
      );
    },
    [getEvidenceUploadPermissionKeyArray]
  );

  const fetchPermissionGroupData = useCallback(
    (id: string) => {
      try {
        const result = getPermissionGroupById(id);
        updatePermCategoryEnabledObj(result?.permissions);
        buildAllPermissions(operationCodeState, result?.permissions);
        updateAllPermissions(operationCodeState, result?.permissions);
      } catch (e) {
        console.error('Failed to fetch permission group data:', e);
      }
    },
    [
      getPermissionGroupById,
      operationCodeState,
      buildAllPermissions,
      updateAllPermissions,
    ]
  );

  useEffect(() => {
    if (
      operationCodeState === OperationCode.edit &&
      permissionGroupId &&
      !isEmptyInstance(permissionsFromDB)
    ) {
      fetchPermissionGroupData(permissionGroupId);
    } else {
      updateAllPermissions(operationCodeState);
    }
  }, [
    fetchPermissionGroupData,
    operationCodeState,
    permissionGroupId,
    permissionsFromDB,
    updateAllPermissions,
  ]);

  const hasNoChange = useMemo((): boolean => {
    if (groupNameError !== null || descriptionError !== null) {
      dispatch(setPermission({property: 'isEditInProgress', value: false}));
      return true;
    }

    if (groupName === '' || description === '') {
      dispatch(setPermission({property: 'isEditInProgress', value: false}));
      return true;
    }

    if (
      originalGroupName.trim() === groupName.trim() &&
      description.trim() === originalDescription.trim() &&
      _.isEqual(
        _.sortBy(budgetPermissionKeyArray),
        _.sortBy(originalBudgetPermissionKeys)
      ) &&
      _.isEqual(
        _.sortBy(evidenceUploadPermissionKeyArray),
        _.sortBy(originalEvidenceUploadPermissionKeys)
      ) &&
      _.isEqual(
        _.mapValues(groupPermValueItems, (perms) =>
          _.mapValues(perms, (p) => p.checked)
        ),
        _.mapValues(orgGroupPermValueItems, (perms) =>
          _.mapValues(perms, (p) => p.checked)
        )
      )
    ) {
      dispatch(setPermission({property: 'isEditInProgress', value: false}));
      return true;
    }

    dispatch(setPermission({property: 'isEditInProgress', value: true}));
    return false;
  }, [
    groupNameError,
    descriptionError,
    groupName,
    description,
    originalGroupName,
    originalDescription,
    budgetPermissionKeyArray,
    originalBudgetPermissionKeys,
    evidenceUploadPermissionKeyArray,
    originalEvidenceUploadPermissionKeys,
    groupPermValueItems,
    orgGroupPermValueItems,
    dispatch,
  ]);

  const onChangeGroupPermItem = useCallback(
    (permissionType: PermissionType, permValueItemObj: GroupPermValueItem) => {
      setGroupPermValueItems((prevGroupPermValueItems) => {
        if (
          _.isEqual(prevGroupPermValueItems?.[permissionType], permValueItemObj)
        ) {
          return prevGroupPermValueItems;
        } else {
          const updatedGroupPermValueItems = {
            ...prevGroupPermValueItems,
            [permissionType]: {...permValueItemObj},
          };
          return updatedGroupPermValueItems;
        }
      });
    },
    []
  );

  const onChangeOverridePerm = useCallback(
    (permissionType: PermissionType, checked: boolean) => {
      setPermCategoryEnabledObj((prevPermCategoryEnabledObj) => {
        const updatedGroupPermValueItems = {
          ...prevPermCategoryEnabledObj,
          [permissionType]: checked,
        };
        return updatedGroupPermValueItems;
      });
    },
    []
  );

  const getPermissionGroupTitle = useMemo(() => {
    if (permCategory === PERMISSION_GROUP_CATEGORY.CLIENT) {
      if (operationCodeState === OperationCode.add) {
        return t('addNewPermissionGroupPage.addNewClientPermGroup');
      } else {
        return t('addNewPermissionGroupPage.editClientPermGroup');
      }
    } else if (permCategory === PERMISSION_GROUP_CATEGORY.PROJECT) {
      if (operationCodeState === OperationCode.add) {
        return t('addNewPermissionGroupPage.addNewProjectPermGroup');
      } else {
        return t('addNewPermissionGroupPage.editProjectPermGroup');
      }
    }
  }, [permCategory, operationCodeState]);

  const qsiCloseBtnId = useMemo(() => {
    if (permCategory === PERMISSION_GROUP_CATEGORY.CLIENT) {
      if (operationCodeState === OperationCode.add) {
        dispatch(
          setPermissionGroupDrawerDntSaveDialogConfirmBtnId(
            QSI_BTN_IDS.CLI_PERM_GROUP.CREATE.YES_CANCEL
          )
        );
        return QSI_BTN_IDS.CLI_PERM_GROUP.CREATE.CLOSE;
      } else {
        dispatch(
          setPermissionGroupDrawerDntSaveDialogConfirmBtnId(
            QSI_BTN_IDS.CLI_PERM_GROUP.EDIT.YES_CANCEL
          )
        );
        return QSI_BTN_IDS.CLI_PERM_GROUP.EDIT.CLOSE;
      }
    } else if (permCategory === PERMISSION_GROUP_CATEGORY.PROJECT) {
      if (operationCodeState === OperationCode.add) {
        dispatch(
          setPermissionGroupDrawerDntSaveDialogConfirmBtnId(
            QSI_BTN_IDS.PROJ_PERM_GROUP.CREATE.YES_CANCEL
          )
        );
        return QSI_BTN_IDS.PROJ_PERM_GROUP.CREATE.CLOSE;
      } else {
        dispatch(
          setPermissionGroupDrawerDntSaveDialogConfirmBtnId(
            QSI_BTN_IDS.PROJ_PERM_GROUP.EDIT.YES_CANCEL
          )
        );
        return QSI_BTN_IDS.PROJ_PERM_GROUP.EDIT.CLOSE;
      }
    }
  }, [permCategory, operationCodeState]);

  useEffect(() => {
    return () => {
      dispatch(setPermissionGroupDrawerDntSaveDialogConfirmBtnId(undefined));
    };
  }, []);

  const qsiSaveBtnId = useMemo(() => {
    if (permCategory === PERMISSION_GROUP_CATEGORY.CLIENT) {
      if (operationCodeState === OperationCode.add) {
        return QSI_BTN_IDS.CLI_PERM_GROUP.CREATE.SAVE;
      } else {
        return QSI_BTN_IDS.CLI_PERM_GROUP.EDIT.SAVE;
      }
    } else if (permCategory === PERMISSION_GROUP_CATEGORY.PROJECT) {
      if (operationCodeState === OperationCode.add) {
        return QSI_BTN_IDS.PROJ_PERM_GROUP.CREATE.SAVE;
      } else {
        return QSI_BTN_IDS.PROJ_PERM_GROUP.EDIT.SAVE;
      }
    }
  }, [permCategory, operationCodeState]);

  const onAddPermissionGroup = useCallback(() => {
    setOperationCodeState(OperationCode.add);
    setGroupName('');
    setGroupNameError('');
    setDescription('');
    setPermCategory(PERMISSION_GROUP_CATEGORY.PROJECT);
  }, [buildAllPermissions, updateAllPermissions, groupName]);

  return (
    <FixedHeaderPanelContainer
      panelHeader={
        <Box className="permission-group-drawer-header">
          <AurButton
            classNames="drawer-close-button"
            label={t('label.close')}
            type="exit"
            size="Medium"
            title={t('label.close')}
            id={qsiCloseBtnId}
            onClick={onClose}
          />
          <AurAskLioDrawerHeaderButton />
        </Box>
      }
      contentHeader={
        <Box className="permission-group-drawer-header permission-group-drawer-sub-header">
          <Typography
            className={'typo-text headline3_size permission-heading'}
            role="heading"
            aria-level={1}
          >
            {getPermissionGroupTitle}
          </Typography>
          <Box className="permission-header-buttons">
            <AurButton
              classNames="typo-text buttonMedium_size dont-save-permission-button"
              label={t('addNewPermissionGroupPage.dontSaveBtn')}
              type="exit"
              size="Medium"
              title={t('addNewPermissionGroupPage.dontSaveBtn')}
              onClick={onCancel}
            />
            <AurButton
              classNames={`typo-text buttonMedium_size ${(isSaving || hasNoChange) && 'save-new-permission-button'}`}
              type="continue"
              size="Medium"
              title={t('addNewPermissionGroupPage.saveBtn')}
              onClick={savePermissionGroup}
              disabled={isSaving || hasNoChange}
              id={qsiSaveBtnId}
              startIcon={isSaving ? <AurLoadingIndicator size={16} /> : ''}
            >
              {t('addNewPermissionGroupPage.saveBtn')}
            </AurButton>
          </Box>
        </Box>
      }
      contentBody={
        <form
          className="add-new-permission-form"
          aria-labelledby="permission-group-form-title"
        >
          <Box className="add-new-permission-group-header">
            {isClientPerm && (
              <Typography className="typo-text body2_size add-new-permission-group-description">
                {t('addNewPermissionGroupPage.clientPermGroupDesc')}
                <Typography
                  onClick={onAddPermissionGroup}
                  className="typo-text body2_size permission-project-perm-link"
                >
                  {t('addNewPermissionGroupPage.projectPermGroupHere')}
                </Typography>
              </Typography>
            )}
            <AurTextField
              required
              label={t('addNewPermissionGroupPage.permGroupName')}
              value={groupName}
              onChange={handlePermissionGroupNameChange}
              variant="filled"
              className="aur-textfield custom-text-field textfield permission-group-textfield"
              ariaLabel={t('addNewPermissionGroupPage.permGroupName')}
              helperText={
                groupNameError ||
                t('addNewPermissionGroupPage.permissionGroupNameMsg')
              }
              error={!!groupNameError}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            />
            <AurTextField
              required
              label={t('addNewPermissionGroupPage.description')}
              value={description}
              onChange={handlePermissionDescriptionChange}
              variant="filled"
              multiline
              minRows={3}
              maxRows={3}
              ariaLabel={t('addNewPermissionGroupPage.description')}
              className="aur-textfield custom-text-field textfield permission-group-textfield"
              helperText={
                descriptionError ||
                t('addNewPermissionGroupPage.descriptionValidationMsg')
              }
              error={!!descriptionError}
            />
          </Box>

          {isLoading &&
          isEmptyInstance(permissionMasterConfig) &&
          isEmptyInstance(permCategory)
            ? null
            : (permissionMasterConfig?.[permCategory]?.perm_categories ?? [])
                .filter(
                  (item) =>
                    item !== PERMISSION_TYPE.ECI &&
                    (isPowerBIReportsEnabled ||
                      item !== PERMISSION_TYPE.BUSINESS_INTELLIGENCE)
                )
                .map((permType: PermissionType) => {
                  return (
                    <PermissionCategoryView
                      {...permissionMasterConfig?.[permCategory]
                        ?.perm_cat_config?.[permType]}
                      key={permType}
                      permCategory={permCategory}
                      permType={permType}
                      permItemArray={updatedPermissionList[permType]}
                      defaultIsOverridenChecked={
                        permCategoryEnabledObj?.[permType] ?? false
                      }
                      preloadedPermSwitches={
                        groupPermValueItems?.[permType] ?? {}
                      }
                      onChangePerms={onChangeGroupPermItem}
                      onChangeOverridePerm={onChangeOverridePerm}
                    />
                  );
                })}

          {!isLoading && isProjectPerm && (
            <Box className="estimated-budget-evidence-source-section-container">
              <Box className="estimated-budget-evidence-source-child-container">
                <EstimatedBudgetView
                  rightToViewEstimatedBudgetDisabled={!budgetConsumptionEnabled}
                  initEstimatedBudgetValue={{
                    rightToViewEstimatedBudget:
                      budgetPermissionKeyArray.includes(
                        BUDGET_ESTIMATED.REPORT_BUDGET_CONSUMPTION
                      ),
                    showBudgetAlerts: budgetPermissionKeyArray.includes(
                      BUDGET_ESTIMATED.REPORT_BUDGET_ALERT
                    ),
                  }}
                  onChangeValue={onChangeBudgetValue}
                />
              </Box>
            </Box>
          )}

          {!isLoading && isProjectPerm && (
            <PermissionCategoryView
              {...permissionMasterConfig?.[permCategory]?.perm_cat_config?.[
                PERMISSION_TYPE.ECI
              ]}
              key={PERMISSION_TYPE.ECI}
              permCategory={permCategory}
              permType={PERMISSION_TYPE.ECI}
              permItemArray={updatedPermissionList[PERMISSION_TYPE.ECI]}
              defaultIsOverridenChecked={
                permCategoryEnabledObj?.[PERMISSION_TYPE.ECI] ?? false
              }
              preloadedPermSwitches={
                groupPermValueItems?.[PERMISSION_TYPE.ECI] ?? {}
              }
              onChangePerms={onChangeGroupPermItem}
              onChangeOverridePerm={onChangeOverridePerm}
            />
          )}

          {!isLoading && isProjectPerm && isEvidenceSourceUploadEnabled && (
            <Box className="estimated-budget-evidence-source-section-container">
              <Box className="estimated-budget-evidence-source-child-container">
                <EvidenceSourceManageView
                  isDisabled={!isEvidenceSourceUploadEnabled}
                  initEvidenceSourceManageValue={{
                    manageProjectEvidence:
                      evidenceUploadPermissionKeyArray.includes(
                        EVIDENCE_SOURCE_MANAGEMENT.STAGING_AREA_ACCESS
                      ),
                    uploadRights: evidenceUploadPermissionKeyArray.includes(
                      EVIDENCE_SOURCE_MANAGEMENT.EVIDENCE_SOURCE_UPLOAD_ACCESS
                    ),
                    initiateProcessingRights:
                      evidenceUploadPermissionKeyArray.includes(
                        EVIDENCE_SOURCE_MANAGEMENT.INITIATE_PROCESSING_ACCESS
                      ),
                  }}
                  onChangeValue={onChangeEvidenceSourceManageValue}
                />
              </Box>
            </Box>
          )}
        </form>
      }
    />
  );
};

export default AddEditPermissionGroup;
